import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  FilterList,
  FileDownloadOutlined,
  PictureAsPdfOutlined,
  BusinessOutlined,
  WarningAmberOutlined,
  CheckCircleOutlined,
  LocationOnOutlined,
  PlayArrow,
} from '@mui/icons-material';

// Export tools
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Recharts components
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from 'recharts';
import {
  generateSectorWideReport,
  getMeasurements,
  getSectorV2,
  getStatePersonalizedV2,
} from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import { getYears } from '../../../../helpers/dateHelpers';

export default function CrossFacilityAnalytics({ props }) {
  const theme = useTheme();
  const dashboardRef = useRef(null);

  const quarters = [
    { id: 1, name: 'Quarter 1' },
    { id: 2, name: 'Quarter 2' },
    { id: 3, name: 'Quarter 3' },
    { id: 4, name: 'Quarter 4' },
  ];

  // Filter States
  const [selectedSector, setSelectedSector] = useState('ALL');
  const [selectedState, setSelectedState] = useState('ALL');
  const [selectedGroup, setSelectedGroup] = useState('ALL');

  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateSelected, setStateSelected] = useState('');

  const [measurementLoading, setMeasurementLoading] = useState(false);
  const [measurementData, setMeasurementData] = useState([]);
  const [measurementSelected, setMeasurementSelected] = useState('');

  const [sectorList, setSectorList] = useState([]);
  const [sectorLoading, setSectorLoading] = useState(false);
  const [sectorSelected, setSectorSelected] = useState('');

  const [quarterSelected, setQuarterSelected] = useState('');
  const [yearSelected, setYearSelected] = useState('');

  const [reportLoading, setReportLoading] = useState(false);

  // Dynamic API Report Data State
  const [reportData, setReportData] = useState([]);

  // console.log('sectorList', sectorList);
  // console.log('stateList', stateList);
  // console.log('measurementDate', measurementData);

  useEffect(() => {
    fetchSectorList();
    fetchMeasurement();
    fetchStateList();
  }, []);

  const fetchSectorList = async () => {
    setSectorLoading(true);
    try {
      const res = await getSectorV2();
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setSectorList(data);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({
        type: 'error',
        message: error?.data?.result?.status?.desc || 'Error fetching states.',
      });
    } finally {
      setSectorLoading(false);
    }
  };

  const fetchStateList = async () => {
    setStateLoading(true);
    try {
      const res = await getStatePersonalizedV2();
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setStateList(data);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({
        type: 'error',
        message: error?.data?.result?.status?.desc || 'Error fetching states.',
      });
    } finally {
      setStateLoading(false);
    }
  };

  const fetchMeasurement = () => {
    setMeasurementLoading(true);
    getMeasurements()
      .then((response) => {
        const responseData = response?.result?.data || [];
        setMeasurementData(responseData);
      })
      .catch((err) => console.error('Failed to fetch measurements', err))
      .finally(() => setMeasurementLoading(false));
  };

  const generateReport = async () => {
    setReportLoading(true);
    try {
      const res = await generateSectorWideReport(
        stateSelected,
        yearSelected,
        quarterSelected,
        sectorSelected
      );
      console.log('res', res);
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        console.log('data', data);
        setReportData(Array.isArray(data) ? data : []);
      } else {
        setReportData([]);
        openNotification({ type: 'error', message: status?.desc || 'Failed to fetch report.' });
      }
    } catch (e) {
      console.log(e);
      setReportData([]);
    } finally {
      setReportLoading(false);
    }
  };

  // Extract Filter Options dynamically from API Dataset
  const sectors = useMemo(
    () => ['ALL', ...new Set(reportData.map((d) => d.sectorName || d.sector).filter(Boolean))],
    [reportData]
  );
  const states = useMemo(
    () => ['ALL', ...new Set(reportData.map((d) => d.stateName || d.state).filter(Boolean))],
    [reportData]
  );
  const groups = useMemo(
    () => ['ALL', ...new Set(reportData.map((d) => d.groupName).filter(Boolean))],
    [reportData]
  );

  // Filter Dataset based on inline client-side filters
  const filteredData = useMemo(() => {
    return reportData.filter((item) => {
      const sec = item.sectorName || item.sector;
      const st = item.stateName || item.state;
      const matchSector = selectedSector === 'ALL' || sec === selectedSector;
      const matchState = selectedState === 'ALL' || st === selectedState;
      const matchGroup = selectedGroup === 'ALL' || item.groupName === selectedGroup;
      return matchSector && matchState && matchGroup;
    });
  }, [reportData, selectedSector, selectedState, selectedGroup]);

  // Aggregated Facility-Level Summary (Groups multiple sample points under single facility)
  const aggregatedFacilityData = useMemo(() => {
    const facilityMap = {};

    filteredData.forEach((item) => {
      const facId = item.facilityId || item.facilityName || 'unknown-facility';
      const facName = item.facilityName || 'N/A';
      const sector = item.sectorName || item.sector || 'N/A';
      const state = item.stateName || item.state || 'N/A';

      if (!facilityMap[facId]) {
        facilityMap[facId] = {
          facilityId: facId,
          facilityName: facName,
          sector,
          state,
          samplePointCount: 0,
          totalTested: 0,
          passed: 0,
          failed: 0,
        };
      }

      facilityMap[facId].samplePointCount += 1;

      const results = item.results?.flatMap((r) => r?.data || []) || [];
      results.forEach((res) => {
        facilityMap[facId].totalTested += 1;
        const isDefault = res.resultStatus === 'EXCEEDED' || res.resultStatus === 'RECHECK';
        if (isDefault) {
          facilityMap[facId].failed += 1;
        } else {
          facilityMap[facId].passed += 1;
        }
      });
    });

    return Object.values(facilityMap);
  }, [filteredData]);

  // Aggregation & KPI Computations
  const analyticsSummary = useMemo(() => {
    let totalFacilities = new Set();
    let totalTests = 0;
    let passedCount = 0;
    let exceedCount = 0;

    const sectorBreakdown = {};
    const facilityExceedances = {};

    filteredData.forEach((point) => {
      if (point.facilityId) totalFacilities.add(point.facilityId);

      const allResults = point.results?.flatMap((r) => r?.data || []) || [];

      allResults.forEach((res) => {
        totalTests++;
        const isDefault = res.resultStatus === 'EXCEEDED' || res.resultStatus === 'RECHECK';

        if (isDefault) {
          exceedCount++;
          const facName = point.facilityName || 'Unknown Facility';
          facilityExceedances[facName] = (facilityExceedances[facName] || 0) + 1;
        } else {
          passedCount++;
        }

        // Aggregate by Sector
        const secName = point.sectorName || point.sector || 'General';
        if (!sectorBreakdown[secName]) {
          sectorBreakdown[secName] = { total: 0, exceed: 0 };
        }
        sectorBreakdown[secName].total++;
        if (isDefault) sectorBreakdown[secName].exceed++;
      });
    });

    const complianceRate = totalTests > 0 ? ((passedCount / totalTests) * 100).toFixed(1) : 0;

    // Format Sector Benchmark Bar Chart Data
    const sectorChartData = Object.keys(sectorBreakdown).map((sec) => ({
      sector: sec,
      total: sectorBreakdown[sec].total,
      exceedances: sectorBreakdown[sec].exceed,
      complianceRate: parseFloat(
        (
          ((sectorBreakdown[sec].total - sectorBreakdown[sec].exceed) /
            sectorBreakdown[sec].total) *
          100
        ).toFixed(1)
      ),
    }));

    // Format Facility Ranking Chart Data
    const facilityRankingData = Object.keys(facilityExceedances).map((fac) => ({
      facilityName: fac,
      exceedances: facilityExceedances[fac],
    }));

    return {
      facilityCount: totalFacilities.size,
      totalTests,
      passedCount,
      exceedCount,
      complianceRate,
      sectorChartData,
      facilityRankingData,
    };
  }, [filteredData]);

  // Excel Export Function (Generates Grouped Facility Export)
  const exportToExcel = () => {
    if (!aggregatedFacilityData || aggregatedFacilityData.length === 0) return;

    const sheetData = [
      [
        'Facility Name',
        'Sector',
        'State',
        'Sample Points Monitored',
        'Total Tested',
        'Passed',
        'Failed (Exceeded/Recheck)',
        'Status',
      ],
    ];

    aggregatedFacilityData.forEach((item) => {
      sheetData.push([
        item.facilityName,
        item.sector,
        item.state,
        item.samplePointCount,
        item.totalTested,
        item.passed,
        item.failed,
        item.failed > 0 ? 'ACTION REQUIRED' : 'COMPLIANT',
      ]);
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Facility_Summary');
    XLSX.writeFile(workbook, `Facility_Grouped_Analytics.xlsx`);
  };

  // PDF Export Function
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;
    const element = dashboardRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Sector_Comparison_Report.pdf`);
  };

  const renderDashboardContent = () => {
    if (reportLoading) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', minHeight: 450 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Generating sector report analytics...
          </Typography>
        </Stack>
      );
    }

    if (!reportData || reportData.length === 0) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%', minHeight: 450, color: 'text.secondary' }}
        >
          <Typography variant="h6">No Analytics Data Generated</Typography>
          <Typography variant="body2">
            Select the required State filter in the top bar and click "Generate Report".
          </Typography>
        </Stack>
      );
    }

    return (
      <Box ref={dashboardRef}>
        {/* Metric Overview Cards */}
        <Grid2 container spacing={2.5} sx={{ mb: 4 }}>
          {/* Card 1: Facilities */}
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      FACILITIES
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
                      {analyticsSummary.facilityCount}
                    </Typography>
                  </Box>
                  <BusinessOutlined color="primary" sx={{ fontSize: 36, opacity: 0.8 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid2>

          {/* Card 2: Overall Compliance Rate */}
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      COMPLIANCE RATE
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
                      {analyticsSummary.complianceRate}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Number(analyticsSummary.complianceRate)}
                      color={Number(analyticsSummary.complianceRate) > 75 ? 'success' : 'warning'}
                      sx={{ mt: 1, height: 6, borderRadius: 1 }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid2>

          {/* Card 3: Total Exceedances */}
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      FAILED
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="error.main" sx={{ mt: 0.5 }}>
                      {analyticsSummary.exceedCount}
                    </Typography>
                  </Box>
                  <WarningAmberOutlined color="error" sx={{ fontSize: 36, opacity: 0.8 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid2>

          {/* Card 4: Passed Tests */}
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      PASSED
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mt: 0.5 }}>
                      {analyticsSummary.passedCount}
                    </Typography>
                  </Box>
                  <CheckCircleOutlined color="success" sx={{ fontSize: 36, opacity: 0.8 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

        {/* Charts Section */}
        <Grid2 container spacing={3} sx={{ mb: 4 }}>
          {/* Sector Compliance Benchmarking */}
          <Grid2 size={{ xs: 12, md: 7 }}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} color="primary.dark" gutterBottom>
                SECTOR COMPLIANCE RATE (%)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Percentage of parameters adhering to regulatory standards per industry sector
              </Typography>
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={analyticsSummary.sectorChartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                  >
                    <XAxis dataKey="sector" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                    <Tooltip formatter={(value) => [`${value}%`, 'Compliance Rate']} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <ReferenceLine
                      y={80}
                      stroke="#ed6c02"
                      strokeDasharray="3 3"
                      label={{ value: 'Target 80%', fill: '#ed6c02', fontSize: 10 }}
                    />
                    <Bar dataKey="complianceRate" name="Compliance Rate" radius={[4, 4, 0, 0]}>
                      {analyticsSummary.sectorChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.complianceRate < 70 ? '#d32f2f' : '#2e7d32'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid2>

          {/* Top Offending Facilities */}
          <Grid2 size={{ xs: 12, md: 5 }}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} color="primary.dark" gutterBottom>
                HIGH-RISK FACILITIES (BY DEFAULTS)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Facilities with the highest number of exceeded parameters
              </Typography>
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    layout="vertical"
                    data={analyticsSummary.facilityRankingData}
                    margin={{ top: 10, right: 30, left: 40, bottom: 20 }}
                  >
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis
                      dataKey="facilityName"
                      type="category"
                      tick={{ fontSize: 10 }}
                      width={120}
                    />
                    <Tooltip formatter={(value) => [`${value} parameter(s)`, 'Exceedances']} />
                    <Bar
                      dataKey="exceedances"
                      name="Exceedance Count"
                      fill="#d32f2f"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid2>
        </Grid2>

        {/* Grouped Facility Compliance Table */}
        <Paper variant="outlined">
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
              FACILITY COMPLIANCE SUMMARY TABLE (GROUPED)
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Facility Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sector</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Sample Points
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Tested
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Passed
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Failed
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aggregatedFacilityData.map((item, index) => {
                  const hasDefault = item.failed > 0;

                  return (
                    <TableRow key={item.facilityId || index} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{item.facilityName}</TableCell>
                      <TableCell>{item.sector}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <LocationOnOutlined
                            sx={{ fontSize: '0.9rem', color: 'text.secondary' }}
                          />
                          <span>{item.state}</span>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${item.samplePointCount} point(s)`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell align="center">{item.totalTested}</TableCell>
                      <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                        {item.passed}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: hasDefault ? 'error.main' : 'text.primary',
                          fontWeight: hasDefault ? 700 : 400,
                          bgcolor: hasDefault ? alpha('#d32f2f', 0.08) : 'transparent',
                        }}
                      >
                        {item.failed}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={hasDefault ? 'ACTION REQUIRED' : 'COMPLIANT'}
                          size="small"
                          color={hasDefault ? 'error' : 'success'}
                          sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: '90vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Sticky Top Header with Inline Controls */}
      <Paper
        square
        elevation={0}
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'white',
          zIndex: 10,
        }}
      >
        <Stack spacing={2}>
          {/* Top Title & Export Action Row */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={700} color="primary.dark">
                Cross-Facility & Sector Analytics
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Comparative environmental compliance benchmarking across sectors, states, and
                facilities
              </Typography>
            </Box>

            {reportData.length > 0 && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<FileDownloadOutlined />}
                  onClick={exportToExcel}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }}
                >
                  Excel
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<PictureAsPdfOutlined />}
                  onClick={exportToPDF}
                  sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }}
                >
                  PDF
                </Button>
              </Stack>
            )}
          </Stack>

          {/* Inline Filter Controls Bar */}
          <Grid2 container spacing={2} alignItems="center">
            <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 2.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>State *</InputLabel>
                <Select
                  label="State *"
                  size="small"
                  value={stateSelected}
                  required
                  onChange={(e) => setStateSelected(e.target.value)}
                  startAdornment={stateLoading && <CircularProgress size={18} sx={{ mr: 1 }} />}
                >
                  <MenuItem value="">Select State</MenuItem>
                  {stateList?.map((i) => (
                    <MenuItem key={i?.stateId} value={i?.stateId}>
                      {i?.stateName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 2.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sector *</InputLabel>
                <Select
                  label="Sector *"
                  size="small"
                  value={sectorSelected}
                  onChange={(e) => setSectorSelected(e.target.value)}
                  startAdornment={sectorLoading && <CircularProgress size={18} sx={{ mr: 1 }} />}
                >
                  <MenuItem value="">Select Sector</MenuItem>
                  {sectorList?.map((m) => (
                    <MenuItem key={m?.sectorId} value={m?.sectorId}>
                      {m?.sectorName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 2, lg: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Quarter *</InputLabel>
                <Select
                  label="Quarter *"
                  size="small"
                  value={quarterSelected}
                  onChange={(e) => setQuarterSelected(e.target.value)}
                >
                  {quarters.map((q) => (
                    <MenuItem key={q.id} value={q.id}>
                      {q.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 2, lg: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Year *</InputLabel>
                <Select
                  label="Year *"
                  size="small"
                  value={yearSelected}
                  onChange={(e) => setYearSelected(e.target.value)}
                >
                  {getYears().map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12, md: 2, lg: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="medium"
                startIcon={
                  reportLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />
                }
                disabled={reportLoading || !stateSelected}
                onClick={generateReport}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, height: 40 }}
              >
                Generate Report
              </Button>
            </Grid2>
          </Grid2>
        </Stack>

        {/* Commented Out / Dynamic API Selects Preserved Intact */}
        {/* <FormControl fullWidth size="small">
          <InputLabel>Industry Sector</InputLabel>
          <Select
            value={selectedSector}
            label="Industry Sector"
            onChange={(e) => setSelectedSector(e.target.value)}
          >
            {sectors.map((sec) => (
              <MenuItem key={sec} value={sec}>
                {sec}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}

        {/* <FormControl fullWidth size="small">
          <InputLabel>State / Location</InputLabel>
          <Select
            value={selectedState}
            label="State / Location"
            onChange={(e) => setSelectedState(e.target.value)}
          >
            {states.map((st) => (
              <MenuItem key={st} value={st}>
                {st}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}

        {/* <FormControl fullWidth size="small">
          <InputLabel>Monitoring Group</InputLabel>
          <Select
            value={selectedGroup}
            label="Monitoring Group"
            onChange={(e) => setSelectedGroup(e.target.value)}
          >
            {groups.map((grp) => (
              <MenuItem key={grp} value={grp}>
                {grp}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}

        {/* <FormControl fullWidth>
          <InputLabel>Measurement *</InputLabel>
          <Select
            label="Measurement *"
            value={measurementSelected}
            onChange={(e) => setMeasurementSelected(e.target.value)}
            required
            startAdornment={measurementLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
          >
            {measurementData?.map((m) => (
              <MenuItem key={m?.measurementId} value={m?.measurementId}>
                {m?.measurementName}
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}
      </Paper>

      {/* Main Full-Width Scrollable Workspace Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          bgcolor: '#f8f9fa',
          p: 3,
        }}
      >
        {renderDashboardContent()}
      </Box>
    </Box>
  );
}
